import { useEffect, useState } from "react";
import {
    Layout,
    Table,
    Input,
    Button,
    Space,
    Tag,
    Card,
    Form,
    Select,
    Breadcrumb,
    Typography,
    Row,
    Col,
    message,
    Spin,
    Popconfirm,
    Modal,
    InputNumber,
    DatePicker,
    Checkbox
} from "antd";

import { APPBACKGROUND_COLOR } from "../appsettings";
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import { Content } from "antd/es/layout/layout";
import { Link } from "react-router-dom";
import {
    SearchOutlined,
    EyeOutlined,
    EditOutlined,
    ClearOutlined,
    FilterOutlined,
    DeleteOutlined,
    PlusOutlined,
    PrinterOutlined
} from "@ant-design/icons";
import axiosClient from "../../api/axiosClient";
import { PDFViewer } from "@react-pdf/renderer";
import TrainerReportPDF from "./trainer_report-pdf";

export const TrainersView = () => {
    const [loading, setLoading] = useState(false);
    const [printingItemId, setPrintingItemId] = useState(null);
    const [trainers, setTrainers] = useState([]);
    const USERID = localStorage.getItem("USERID");
    const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
    const [selectedMemberForMetrics, setSelectedMemberForMetrics] = useState(null);
    const [metricsForm] = Form.useForm();
    const [printPreview, setPrintPreview] = useState(false);
    const [selectedTrainerForPrint, setSelectedTrainerForPrint] = useState(null);

    // Print options state
    const [includeMetrics, setIncludeMetrics] = useState(true);
    const [includeAssignments, setIncludeAssignments] = useState(true);
    const [assignedMembersForPrint, setAssignedMembersForPrint] = useState([]);

    const [filters, setFilters] = useState({ search: "", status: "all", gender: "all" });

    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [form] = Form.useForm();

    const fetchTrainers = async (page = 1, pageSize = 10, search = "", status = "", gender = "") => {
        try {
            setLoading(true);

            const url = `/trainer/get_all_trainers.php?page=${page}&pageSize=${pageSize}&search=${search}&status=${status}&gender=${gender}`;

            const response = await axiosClient.get(url);
            const result = response.data;

            setTrainers(result.data || []);
            setPagination({ current: page, pageSize, total: result.total || 0 });
        } catch (err) {
            console.error("Failed to fetch trainers", err);
            message.error("Could not load data");
        } finally {
            setLoading(false);
        }
    };

    const handlePrintPreview = async (id) => {
        try {
            setPrintingItemId(id);
            const response = await axiosClient.get(
                `/trainer/get_trainer_by_id_with_metrics.php?id=${id}`
            );
            const result = response.data;
            setSelectedTrainerForPrint(result.data || []);

            // Fetch Assigned Members
            try {
                const membersResponse = await axiosClient.get(`/trainer/get_assigned_members.php?trainer_id=${id}`);
                if (membersResponse.data.status === "success") {
                    setAssignedMembersForPrint(membersResponse.data.data || []);
                } else {
                    setAssignedMembersForPrint([]);
                }
            } catch (error) {
                console.error("Error fetching assigned members for print:", error);
                setAssignedMembersForPrint([]);
            }

            setPrintPreview(true);
        } catch (err) {
            console.error("Failed to fetch trainer for print", err);
            message.error(err.response?.data?.message || "Failed to fetch trainer for print");
        }finally{
            setPrintingItemId(null);
        }
    }

    const handleMetricsSubmit = async (values) => {
        try {
            setLoading(true);
            const payload = {
                trainer_id: Number.parseInt(selectedMemberForMetrics.id),
                ...values,
                date: values.date ? values.date.format("YYYY-MM-DD HH:mm:ss") : null,
                created_by: USERID
            };
            const response = await axiosClient.post(`/trainer/metric/add_trainer_metric.php`, payload);
            const data = response.data;
            if (data.status === "success") {
                message.success(data.message);
                setIsMetricsModalOpen(false);
                fetchTrainers(pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender);
            } else {
                message.error(data.message);
            }
        } catch (err) {
            console.error("Failed to add metrics", err);
            message.error(err.response?.data?.message || "Could not add metrics");
        } finally {
            setLoading(false);
        }
    };

    const calculateBMI = (changedValues, allValues) => {
        const { weight, height } = allValues;
        if (weight && height) {
            const heightInMeters = height / 100;
            const bmi = (weight / (heightInMeters * heightInMeters)).toFixed(2);
            metricsForm.setFieldsValue({ bmi: bmi });
        }
    };

    const deleteTrainer = async (trainerId) => {
        try {
            setLoading(true);
            const response = await axiosClient.post(`/trainer/delete_trainer_by_id.php?id=${trainerId}`, {
                id: trainerId,
                by: USERID
            });
            const data = response.data;
            if (data.status === "success") {
                message.success(data.message);
                fetchTrainers(pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender)
            } else {
                message.error(data.message);
            }
        } catch (error) {
            console.error("Delete error:", error);
            message.error(error.response?.data?.message || "Failed to delete trainer");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        fetchTrainers(pagination.current, pagination.pageSize, filters.search, filters.status, filters.gender);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.current, pagination.pageSize]);

    const columns = [
        { title: "Trainer ID", dataIndex: "trainer_id", responsive: ["sm"] },
        { title: "Name", dataIndex: "name" },
        { title: "NIC", dataIndex: "nic", responsive: ["md"] },
        { title: "Contact", dataIndex: "contact_no", responsive: ["md"] },
        {
            title: "Gender",
            dataIndex: "gender",
            render: (value) =>
                value === "M" ? <Tag color="blue">Male</Tag> :
                    value === "F" ? <Tag color="pink">Female</Tag> :
                        <Tag color="green">Other</Tag>,
            responsive: ["sm"]
        },
        {
            title: "Status",
            dataIndex: "status",
            render: (value) => (
                <Tag color={value === 1 || value === true ? "green" : "red"}>
                    {value === 1 || value === true ? "ACTIVE" : "INACTIVE"}
                </Tag>
            ),
            responsive: ["sm"]
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, r) => (
                <Space>
                    <Button size="small" 
                    icon={<PrinterOutlined />} loading={printingItemId === r.id} onClick={() => handlePrintPreview(r.id)} />
                    <Link to={`/trainers/view/${r.id}`}><Button type="primary" size="small" icon={<EyeOutlined />} /></Link>
                    <Link to={`/trainers/edit/${r.id}`}><Button
                        style={{
                            backgroundColor: "#FFC107",
                            borderColor: "#FFC107",
                            color: "white",
                        }}
                        size="small"
                        icon={<EditOutlined />}
                    /></Link>
                    <Button
                        size="small"
                        type="primary"
                        style={{
                            backgroundColor: "#d200eeff",
                            borderColor: "#d200eeff",
                            color: "white",
                        }}
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setSelectedMemberForMetrics(r);
                            setIsMetricsModalOpen(true);
                        }}
                    />
                    <Popconfirm
                        title="Are you sure to delete this member?"
                        onConfirm={() => deleteTrainer(r.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            icon={<DeleteOutlined />}
                            size="small"
                            type="primary"
                            danger />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <Spin spinning={loading} tip="Loading...">
            <Layout style={{ backgroundColor: APPBACKGROUND_COLOR, minHeight: "100vh" }}>

                <Headerdiv />
                <Content style={{ padding: "10px" }}>
                    <Breadcrumb style={{ marginBottom: 10 }}>
                        <Breadcrumb.Item><Link to={"/trainers/view"}>Trainers</Link></Breadcrumb.Item>
                        <Breadcrumb.Item><Typography.Text>Trainer List</Typography.Text></Breadcrumb.Item>
                    </Breadcrumb>

                    {/* Filters */}
                    <Card style={{ marginBottom: 10 }} size="small" title="Filter Trainers" extra={
                        <Link to="/trainers/create">
                            <Button icon={<PlusOutlined />} type="primary">Add Trainer</Button>
                        </Link>
                    }>
                        <Form form={form} layout="vertical">
                            <Row gutter={[16, 0]}>

                                {/* Search Input */}
                                <Col xs={24} sm={12} md={8}>
                                    <Form.Item name="search" label="Search">
                                        <Input
                                            placeholder="Search by name, NIC or ID"
                                            prefix={<SearchOutlined />}
                                            allowClear
                                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                        />
                                    </Form.Item>
                                </Col>

                                {/* Status Filter */}
                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="status" label="Status" initialValue="all">
                                        <Select
                                            onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                                            options={[
                                                { label: "All Status", value: "all" },
                                                { label: "Active", value: "active" },
                                                { label: "Inactive", value: "inactive" },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                {/* NEW: Gender Filter */}
                                <Col xs={12} sm={6} md={4}>
                                    <Form.Item name="gender" label="Gender" initialValue="all">
                                        <Select
                                            onChange={(value) => setFilters(prev => ({ ...prev, gender: value }))}
                                            options={[
                                                { label: "All Genders", value: "all" },
                                                { label: "Male", value: "M" },
                                                { label: "Female", value: "F" },
                                            ]}
                                        />
                                    </Form.Item>
                                </Col>

                                {/* Buttons */}
                                <Col xs={24} sm={24} md={8} style={{ display: 'flex', alignItems: 'center' }}>
                                    <Space style={{ marginTop: 5 }}>
                                        <Button icon={<ClearOutlined />} onClick={() => {
                                            form.resetFields();
                                            setFilters({ search: "", status: "all", gender: "all" });
                                        }}>
                                            Reset
                                        </Button>
                                        <Button
                                            icon={<FilterOutlined />}
                                            type="primary"
                                            onClick={() => fetchTrainers(1, pagination.pageSize, filters.search, filters.status, filters.gender)}
                                        >
                                            Filter
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>

                    {/* Table */}
                    <Card title="Trainers List" size="small">
                        <Table
                            loading={loading}
                            columns={columns}
                            dataSource={trainers}
                            rowKey="id"
                            bordered
                            size="small"
                            scroll={{ x: 800 }}
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: pagination.total,
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} trainers`,
                                onChange: (page, pageSize) => setPagination({ ...pagination, current: page, pageSize })
                            }}
                        />
                    </Card>

                    <Modal
                        title={`Add Metrics for ${selectedMemberForMetrics?.name}`}
                        open={isMetricsModalOpen}
                        onCancel={() => setIsMetricsModalOpen(false)}
                        footer={null}
                    >
                        <Form
                            form={metricsForm}
                            layout="vertical"
                            onFinish={handleMetricsSubmit}
                            onValuesChange={calculateBMI}
                        >
                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item
                                        name="date"
                                        label="Date & Time"
                                        rules={[{ required: true, message: 'Please select date and time' }]}
                                    >
                                        <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="weight"
                                        label="Weight (kg)"
                                        rules={[{ required: true, message: 'Please enter weight' }]}
                                    >
                                        <InputNumber placeholder="65kg" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="height"
                                        label="Height (cm)"
                                        rules={[{ required: true, message: 'Please enter height' }]}
                                    >
                                        <InputNumber placeholder="180cm" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="bmi"
                                        label="BMI"
                                    >
                                        <InputNumber placeholder="20%" style={{ width: '100%' }} readOnly />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="body_fat"
                                        label="Body Fat (%)"
                                    >
                                        <InputNumber placeholder="20%" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="muscle_mass"
                                        label="Muscle Mass (kg)"
                                    >
                                        <InputNumber placeholder="20%" style={{ width: '100%' }} min={0} step={0.1} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row justify="end">
                                <Space>
                                    <Button onClick={() => setIsMetricsModalOpen(false)}>Cancel</Button>
                                    <Button type="primary" htmlType="submit" loading={loading}>
                                        Save Metrics
                                    </Button>
                                </Space>
                            </Row>
                        </Form>

                    </Modal>

                    <Modal
                        title="Trainer Print Preview"
                        open={printPreview}
                        onCancel={() => setPrintPreview(false)}
                        footer={null}
                        width={1000}
                        centered
                        destroyOnHidden
                        height={400}
                    >
                         <div style={{ marginBottom: 15, display: 'flex', gap: 10, alignItems: 'center' }}>
                            <Typography.Text strong>Print Options:</Typography.Text>
                            <Checkbox 
                                checked={includeMetrics} 
                                onChange={(e) => setIncludeMetrics(e.target.checked)}
                            >
                                Include Metrics History
                            </Checkbox>
                            <Checkbox 
                                checked={includeAssignments} 
                                onChange={(e) => setIncludeAssignments(e.target.checked)}
                            >
                                Include Assigned Members
                            </Checkbox>
                        </div>
                        {selectedTrainerForPrint && (
                            <PDFViewer key={`${includeMetrics}-${includeAssignments}`} width="100%" height="500px" showToolbar={true}>
                                <TrainerReportPDF 
                                    trainer={selectedTrainerForPrint} 
                                    assignedMembers={assignedMembersForPrint} 
                                    showMetrics={includeMetrics}
                                    showAssignments={includeAssignments}
                                />
                            </PDFViewer>
                        )}
                    </Modal>
                </Content>
                <Footerdiv />
            </Layout>
        </Spin>
    );
};